import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { QUERY_GC_TIME_MS } from '@/config/constants'
import { requireAuthToken } from '@/lib/api'
import { cobaltConnectionKeys } from '@/lib/queryKeys'
import {
  cancelCobaltSync,
  createCobaltConnectToken,
  exchangeCobaltPublicToken,
  fetchCobaltSyncConfigs,
  fetchCobaltSyncs,
  startCobaltSync,
  updateCobaltSyncConfig,
} from '@/features/connections/api'
import type {
  CobaltConnectTokenRequest,
  CobaltSyncAvailabilityWindows,
  CobaltSyncConfigUpdate,
  CobaltSyncEntity,
  CobaltSyncStatus,
} from './types'

const COBALT_PENDING_SYNC_POLL_INTERVAL_MS = 2_000
const COBALT_RUNNING_SYNC_POLL_INTERVAL_MS = 10_000

export function getCobaltSyncPollIntervalMs(
  statuses: ReadonlyArray<CobaltSyncStatus | null | undefined>
): number | false {
  if (
    statuses.some((status) => status === 'queued' || status === 'dispatching')
  ) {
    return COBALT_PENDING_SYNC_POLL_INTERVAL_MS
  }
  if (statuses.includes('running')) return COBALT_RUNNING_SYNC_POLL_INTERVAL_MS
  return false
}

/**
 * Saved lookbacks select the referral and order availability windows.
 * Undefined uses Relay defaults; each window is part of the cache key.
 */
export function useCobaltSyncsQuery(
  enabled: boolean,
  windows: CobaltSyncAvailabilityWindows = {}
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: cobaltConnectionKeys.syncs(organization?.id, windows),
    queryFn: async ({ signal }) => {
      const token = await requireAuthToken(getToken)
      return fetchCobaltSyncs(token, windows, signal)
    },
    enabled: enabled && !!organization?.id,
    staleTime: 0,
    gcTime: QUERY_GC_TIME_MS,
    // This is one aggregate query, so the most urgent entity sets its timer.
    // Once queued work reaches running, the less frequent progress poll wins.
    refetchInterval: (query) =>
      getCobaltSyncPollIntervalMs(
        query.state.data?.entities.map(
          ({ latest_run }) => latest_run?.status
        ) ?? []
      ),
    retry: false,
  })
}

export function useCobaltSyncConfigsQuery(enabled: boolean) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: cobaltConnectionKeys.configs(organization?.id),
    queryFn: async ({ signal }) => {
      const token = await requireAuthToken(getToken)
      return fetchCobaltSyncConfigs(token, signal)
    },
    enabled: enabled && !!organization?.id,
    staleTime: 0,
    gcTime: QUERY_GC_TIME_MS,
    retry: false,
  })
}

export function useUpdateCobaltSyncConfigMutation() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      entity,
      changes,
    }: {
      entity: CobaltSyncEntity
      changes: CobaltSyncConfigUpdate
    }) => {
      const token = await requireAuthToken(getToken)
      return updateCobaltSyncConfig(token, entity, changes)
    },
    onSuccess: async (_data, { entity, changes }) => {
      await queryClient.invalidateQueries({
        queryKey: cobaltConnectionKeys.configs(organization?.id),
      })
      // Orders availability is counted only while its sync is enabled.
      // Refresh when that flag or lookahead changes; other schedule fields
      // do not affect availability and need no live Cobalt count read.
      //
      // Lookback is excluded too, but for a different reason: it is part of
      // the syncs query key, so the saved value is fetched under a new entry
      // on the next render. Invalidating here would only refresh the entry
      // that render is about to abandon.
      if (
        !('lookahead_days' in changes) &&
        !(entity === 'orders' && 'enabled' in changes)
      )
        return
      await queryClient.invalidateQueries({
        queryKey: cobaltConnectionKeys.syncsAll(organization?.id),
      })
    },
  })
}

export function useStartCobaltSyncMutation() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (entity: CobaltSyncEntity) => {
      const token = await requireAuthToken(getToken)
      return startCobaltSync(token, entity)
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: cobaltConnectionKeys.syncsAll(organization?.id),
      }),
  })
}

export function useCancelCobaltSyncMutation() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (entity: CobaltSyncEntity) => {
      const token = await requireAuthToken(getToken)
      return cancelCobaltSync(token, entity)
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: cobaltConnectionKeys.syncsAll(organization?.id),
      }),
  })
}

export function useCobaltConnectTokenMutation() {
  const { getToken } = useAuth()

  return useMutation({
    mutationFn: async (request: CobaltConnectTokenRequest) => {
      const token = await requireAuthToken(getToken)
      return createCobaltConnectToken(token, request)
    },
  })
}

export function useCobaltTokenExchangeMutation() {
  const { getToken } = useAuth()

  return useMutation({
    mutationFn: async (publicToken: string) => {
      const token = await requireAuthToken(getToken)
      return exchangeCobaltPublicToken(token, publicToken)
    },
  })
}
